import React, { useState, useEffect, useCallback } from 'react';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Flag, { FlagGroup } from '@atlaskit/flag';
import Tick from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { compose, graphql, withApollo } from 'react-apollo';
import { useSetState } from 'react-use';
import TableFilters from '../components/virtual-components/TableFilters';
import { SearchRow } from './VirtualComponentHierarchy.styles';
import {
    UPDATE_MULTIPLE_VIRTUAL_COMPONENTS,
    UPDATE_VIRTUAL_LABEL,
    UPDATE_VIRTUAL_COMPONENT,
    DELETE_VIRTUAL_COMPONENT,
    MERGE_VIRTUAL_COMPONENT,
    DELETE_VIRTUAL_LABEL,
    SET_ACCESS_AUDIT,
} from '../graphql/mutations';
import { GREEN, SYNC_ERRORS_MESSAGES, RED } from '../utils/consts';
import { ErrorParser } from '../utils/ErrorParser';
import { analytics } from '../services/analytics';
import VirtualComponentForm from '../components/virtual-components/VirtualComponentForm';
import VirtualComponentEdit from '../components/virtual-components/VirtualComponentEdit';
import VirtualComponentsHierarchyTable from '../components/virtual-component-hierarchy/VirtualComponentsHierarchyTable';
import DeleteComponentModal from '../components/virtual-components/DeleteComponentModal';
import MergeComponentModal from '../components/virtual-components/MergeComponentModal';
import DeleteLabelModal from '../components/virtual-components/DeleteLabelModal';

const ComposedVirtualComponentsHierarchyTable = compose(
    graphql(UPDATE_VIRTUAL_COMPONENT, { name: 'updateVirtualComponent', props: ({ data }) => ({ ...data }) }),
    graphql(UPDATE_MULTIPLE_VIRTUAL_COMPONENTS, { name: 'updateMultipleVirtualComponents' }),
    graphql(UPDATE_VIRTUAL_LABEL, { name: 'updateVirtualLabel' }),
    withApollo,
)(VirtualComponentsHierarchyTable);

const initialDataState = {
    filters: {},
    pagination: {
        pageSize: 15,
        page: 0,
    },
};

const VirtualComponentHierarchyPage = (props) => {
    /* State */
    const [dataState, setDataState] = useSetState(initialDataState);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [flags, setFlags] = useState([]);
    const [virtualComponentBeingEdited, setVirtualComponentBeingEdited] = useState(null);

    const [edited, setEdited] = useState(false);
    const [shouldUpdateTable, setShouldUpdateTable] = useState(false);
    const [shouldGroupByLabels, setShouldGroupByLabels] = useState(true);

    // DELETE COMPONENT
    const [isDeleteComponentModalOpen, setIsDeleteComponentModalOpen] = useState(false);
    const [virtualComponentBeingDeleted, setVirtualComponentBeingDeleted] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    // DELETE LABEL
    const [isDeleteLabelModalOpen, setIsDeleteLabelModalOpen] = useState(false);
    const [virtualLabelBeingDeleted, setVirtualLabelBeingDeleted] = useState(null);
    const [deleteLabelLoading, setDeleteLabelLoading] = useState(false);
    // MERGE COMPONENT
    const [isMergeComponentModalOpen, setIsMergeComponentModalOpen] = useState(false);
    const [virtualComponentBeingMerged, setVirtualComponentBeingMerged] = useState(null);
    const [mergeDuplicates, setMergeDuplicates] = useState(null);

    const handleFilterChange = (value, meta) => {
        const getFirstPageConfig = (prev) => {
            if (prev?.pagination?.page === 0) {
                return prev;
            }
            return { ...prev, pagination: { ...prev.pagination, page: 0 } };
        };
        switch (meta.action) {
            case 'add':
                setDataState((prev) => ({
                    ...getFirstPageConfig(prev),
                    filters: {
                        ...prev.filters,
                        [meta.key]: meta.data,
                    },
                }));
                break;
            case 'remove':
                setDataState((prev) => {
                    const { filters } = prev;
                    delete filters[meta.key];
                    return {
                        ...getFirstPageConfig(prev),
                        filters: { ...filters },
                    };
                });
                break;
            case 'update':
            case 'clear':
                setDataState((prev) => ({ ...getFirstPageConfig(prev), filters: value }));
                break;
            case 'toogle-group-labels':
                setShouldGroupByLabels(value);
                break;
            default:
        }
    };

    const handleSetEdited = useCallback((value) => {
        setEdited(value);
    }, []);

    const handleSetFlag = useCallback((flag) => {
        setFlags([flag]);
    }, []);

    const onCreateComponentWithSuccess = useCallback(() => {
        setShouldUpdateTable(true);
        setFlags([
            {
                icon: <Tick primaryColor={GREEN} />,
                title: 'Component created with success.',
                key: 'success',
            },
        ]);
    }, []);

    const setToDelete = useCallback((vc) => {
        setVirtualComponentBeingDeleted(vc);
        setIsDeleteComponentModalOpen(true);
    }, []);

    const setToMerge = useCallback((vc) => {
        setVirtualComponentBeingMerged(vc);
        setIsMergeComponentModalOpen(true);
    }, []);

    const setToDeleteLabel = useCallback((label) => {
        setVirtualLabelBeingDeleted(label);
        setIsDeleteLabelModalOpen(true);
    }, []);

    useEffect(() => {
        if (flags.length) {
            const filteredFlags = flags.filter((flag) => {
                return flag.autoDismiss !== undefined && flag.autoDismiss === false;
            });
            if (filteredFlags.length !== flags.length) {
                setTimeout(() => setFlags(filteredFlags), 5000);
            }
        }
    }, [flags]);

    useEffect(() => {
        if (edited) {
            setShouldUpdateTable(true);
            setEdited(false);
        }
    }, [edited]);

    const editVirtualComponent = useCallback((node) => {
        setVirtualComponentBeingEdited(node);

        analytics.track({
            userId: window.license,
            event: 'accessed_component',
            timestamp: new Date(),
            properties: {
                addon_key: process.env.REACT_APP_KEY,
            },
        });
    }, []);

    const setIsSidePanelOpen = (isOpen) => {
        if (isOpen) {
            props.putContentToRightSidePanel(
                <VirtualComponentEdit
                    virtualComponentBeingEdited={virtualComponentBeingEdited}
                    editVirtualComponent={editVirtualComponent}
                    handleSetEdited={handleSetEdited}
                    handleSetFlag={handleSetFlag}
                    setToDelete={setToDelete}
                    setToMerge={setToMerge}
                    synchronizeComponents={props.synchronize}
                    skipVirtualLabelsQueryRefetch
                    skipVirtualComponentsQueryRefetch
                    skipVirtualComponentQueryRefetch
                />,
                null,
                true,
            );
        } else {
            props.hideRightSidePanel();
        }
    };

    useEffect(() => {
        if (virtualComponentBeingEdited) setIsSidePanelOpen(true);
        if (!virtualComponentBeingEdited) setIsSidePanelOpen(false);
    }, [virtualComponentBeingEdited]);

    useEffect(() => {
        if (shouldUpdateTable) setShouldUpdateTable(false);
    }, [shouldUpdateTable]);

    const handleDeleteLabel = useCallback((id) => {
        setDeleteLabelLoading(true);
        props
            .deleteVirtualLabel({
                variables: { id },
            })
            .then(() => {
                setIsDeleteLabelModalOpen(false);
                setDeleteLabelLoading(false);
                setVirtualLabelBeingDeleted(null);
                setShouldUpdateTable(true);
                setFlags([
                    {
                        icon: <Tick primaryColor={GREEN} />,
                        title: 'Label deleted successfully.',
                        key: 'success',
                    },
                ]);
            })
            .catch((err) => {
                setDeleteLabelLoading(false);
                console.log('delete error: ', err);
                setFlags([
                    {
                        icon: <ErrorIcon primaryColor={RED} />,
                        title: 'An error occurred.',
                        key: 'error',
                    },
                ]);
            });
    }, []);

    const handleDeleteComponent = useCallback((id) => {
        setDeleteLoading(true);
        props
            .deleteVirtualComponent({
                variables: { id },
                awaitRefetchQueries: true,
            })
            .then(() => {
                setIsDeleteComponentModalOpen(false);
                setDeleteLoading(false);
                setVirtualComponentBeingDeleted(null);
                setVirtualComponentBeingEdited(null);
                setShouldUpdateTable(true);
                setFlags([
                    {
                        icon: <Tick primaryColor={GREEN} />,
                        title: 'Component deleted successfully.',
                        key: 'success',
                    },
                ]);
            })
            .catch((err) => {
                setDeleteLoading(false);
                console.log('delete error: ', err);
                if (err?.message && SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)]) {
                    setFlags([
                        {
                            icon: <WarningIcon primaryColor={RED} />,
                            title: SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)].title,
                            description: SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)].message,
                            key: 'Warning',
                            autoDismiss: false,
                            actions: [
                                {
                                    content: 'Dismiss',
                                    onClick: () => {
                                        setFlags([]);
                                    },
                                },
                            ],
                        },
                    ]);
                } else {
                    setFlags([
                        {
                            icon: <ErrorIcon primaryColor={RED} />,
                            title: 'An error occurred.',
                            key: 'error',
                        },
                    ]);
                }
            });
    }, []);

    const handleMergeComponent = useCallback(
        async (parentComponentId, targetComponentId) => {
            setMergeDuplicates(null);

            try {
                const res = await props.mergeVirtualComponent({
                    variables: { parentComponentId, targetComponentId },
                    awaitRefetchQueries: true,
                });

                if (res.data.merge.success) {
                    setIsMergeComponentModalOpen(false);
                    setVirtualComponentBeingMerged(null);
                    setShouldUpdateTable(true);
                    setFlags([
                        {
                            icon: <Tick primaryColor={GREEN} />,
                            title: 'Components merged successfully.',
                            key: 'success',
                        },
                    ]);
                    analytics.track({
                        userId: window.license,
                        event: 'merged_component',
                        timestamp: new Date(),
                        properties: {
                            addon_key: process.env.REACT_APP_KEY,
                        },
                    });
                } else {
                    setMergeDuplicates(res.data.merge.data);
                    setFlags([
                        {
                            icon: <ErrorIcon primaryColor={RED} />,
                            title: 'An error occurred.',
                            key: 'error',
                        },
                    ]);
                }
            } catch (err) {
                console.log('delete error: ', err);
                if (err?.message && SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)]) {
                    setFlags([
                        {
                            icon: <WarningIcon primaryColor={RED} />,
                            title: SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)].title,
                            description: SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)].message,
                            key: 'Warning',
                            autoDismiss: false,
                            actions: [
                                {
                                    content: 'Dismiss',
                                    onClick: () => {
                                        setFlags([]);
                                    },
                                },
                            ],
                        },
                    ]);
                } else {
                    setFlags([
                        {
                            icon: <ErrorIcon primaryColor={RED} />,
                            title: "We couldn't finish the update",
                            description:
                                "This usually happens when components are updated from Jira and aren't synchronized yet. If this problem persist contact support on https://apps.appf.re/oc/support/p2",
                            key: 'error',
                            actions: [{ content: 'Synchronize components', onClick: () => props.synchronize() }],
                            autoDismiss: false,
                        },
                    ]);
                }
            }
        },
        [props],
    );

    const onPageChange = useCallback(
        (page, pageSize) => {
            setDataState((prev) => {
                if (prev.pagination.page !== page || prev.pagination.pageSize !== pageSize) {
                    return { ...prev, pagination: { page, pageSize } };
                }
                return prev;
            });
        },
        [setDataState],
    );

    return (
        <>
            <SearchRow>
                <TableFilters
                    value={dataState.filters}
                    handleChange={handleFilterChange}
                    shouldGroupByLabels={shouldGroupByLabels}
                    showGroupByLabelsToogle={true}
                />
            </SearchRow>
            <ComposedVirtualComponentsHierarchyTable
                filters={dataState.filters}
                shouldGroupByLabels={shouldGroupByLabels}
                shouldUpdateTable={shouldUpdateTable}
                editVirtualComponent={editVirtualComponent}
                setToDelete={setToDelete}
                setToDeleteLabel={setToDeleteLabel}
                pagination={dataState.pagination}
                onPageChange={onPageChange}
            />

            {isFormOpen ? (
                <VirtualComponentForm onSuccess={onCreateComponentWithSuccess} setIsOpen={setIsFormOpen} />
            ) : null}
            {isDeleteComponentModalOpen ? (
                <DeleteComponentModal
                    vc={virtualComponentBeingDeleted}
                    setIsOpen={setIsDeleteComponentModalOpen}
                    loading={deleteLoading}
                    confirm={handleDeleteComponent}
                />
            ) : null}
            {isMergeComponentModalOpen ? (
                <MergeComponentModal
                    virtualComponent={virtualComponentBeingMerged}
                    setIsOpen={setIsMergeComponentModalOpen}
                    duplicates={mergeDuplicates}
                    confirm={handleMergeComponent}
                />
            ) : null}
            {isDeleteLabelModalOpen ? (
                <DeleteLabelModal
                    label={virtualLabelBeingDeleted}
                    setIsOpen={setIsDeleteLabelModalOpen}
                    loading={deleteLabelLoading}
                    confirm={handleDeleteLabel}
                />
            ) : null}
            <FlagGroup>
                {flags.map((flag) => (
                    <Flag key={flag.key} {...flag} />
                ))}
            </FlagGroup>
        </>
    );
};

export default compose(
    graphql(DELETE_VIRTUAL_COMPONENT, { name: 'deleteVirtualComponent' }),
    graphql(DELETE_VIRTUAL_LABEL, { name: 'deleteVirtualLabel' }),
    graphql(MERGE_VIRTUAL_COMPONENT, { name: 'mergeVirtualComponent' }),
    graphql(SET_ACCESS_AUDIT, { name: 'setAccessAudit' }),
)(VirtualComponentHierarchyPage);
